import type {
  Club,
  ClubOffering,
  ClubOwnerSetupTaskId,
  ClubSetupPageTourKey,
  Organization,
} from '@court-sync/shared';
import { resolveClubSetupTaskTourKey } from '@court-sync/shared';

export interface ClubOwnerSetupTask {
  id: ClubOwnerSetupTaskId;
  title: string;
  description: string;
  href: string;
  done: boolean;
  /** Page tour that auto-starts on first visit to `href` (see tours skill). */
  pageTourKey?: ClubSetupPageTourKey | null;
}

export interface BuildClubOwnerSetupTasksInput {
  organization: Pick<Organization, 'description' | 'phone' | 'logoUrl' | 'stripeConnectAccountId' | 'onboardingComplete'> | null;
  club: Pick<Club, 'guestCheckoutEnabled' | 'membershipOnlineJoinEnabled'> | null;
  courtsCount: number;
  activeSportsCount: number;
  membershipPlansCount?: number;
  inventoryItemsCount?: number;
  courtHireRatesCount?: number;
  offerings?: ClubOffering[];
  hasPaymentProcessing: boolean;
}

function organizationProfileDone(
  organization: BuildClubOwnerSetupTasksInput['organization']
): boolean {
  if (!organization) return false;
  return Boolean(
    organization.description?.trim() ||
      organization.phone?.trim() ||
      organization.logoUrl?.trim()
  );
}

function stripeConnectDone(
  organization: BuildClubOwnerSetupTasksInput['organization'],
  hasPaymentProcessing: boolean
): boolean {
  if (!organization) return false;
  if (!hasPaymentProcessing) return true;
  return Boolean(organization.stripeConnectAccountId && organization.onboardingComplete);
}

const CLUB_ONLY_SETUP_TASK_IDS = new Set<ClubOwnerSetupTaskId>([
  'court_hire',
  'memberships',
  'inventory',
]);

export interface OwnerSetupChecklistTask {
  id: string;
  title: string;
  description: string;
  href: string;
  done: boolean;
  pageTourKey?: string | null;
}

/** Appends club-only setup tasks to the academy checklist for combined orgs. */
export function mergeAcademyAndClubSetupTasks(
  academyTasks: OwnerSetupChecklistTask[],
  clubTasks: ClubOwnerSetupTask[]
): OwnerSetupChecklistTask[] {
  const academyTaskIds = new Set(academyTasks.map((task) => task.id));
  const clubTasksToAdd = clubTasks.filter((task) => {
    if (CLUB_ONLY_SETUP_TASK_IDS.has(task.id)) {
      return true;
    }
    if (task.id === 'organization_profile' && academyTaskIds.has('academy_profile')) {
      return false;
    }
    if (task.id === 'stripe_connect' && academyTaskIds.has('payments')) {
      return false;
    }
    if (task.id === 'sports' || task.id === 'courts') {
      return false;
    }
    return !academyTaskIds.has(task.id);
  });

  return [...academyTasks, ...clubTasksToAdd];
}

export function buildClubOwnerSetupTasks(
  input: BuildClubOwnerSetupTasksInput
): ClubOwnerSetupTask[] {
  const {
    organization,
    courtsCount,
    activeSportsCount,
    membershipPlansCount = 0,
    inventoryItemsCount = 0,
    courtHireRatesCount = 0,
    offerings = ['court_hire'],
    hasPaymentProcessing,
  } = input;

  const tasks: ClubOwnerSetupTask[] = [
    {
      id: 'sports',
      title: 'Configure sports offered',
      description: 'Confirm which racquet sports your facility offers and set a primary sport.',
      href: '/admin/organization/settings?tab=sports',
      done: activeSportsCount > 0,
    },
    {
      id: 'courts',
      title: 'Add courts and locations',
      description: 'Register courts at your facility so court hire and scheduling can use them.',
      href: '/admin/courts',
      done: courtsCount > 0,
    },
    {
      id: 'organization_profile',
      title: 'Complete organization profile',
      description: 'Add contact details and branding for your public club page.',
      href: '/admin/organization/settings?tab=basic',
      done: organizationProfileDone(organization),
    },
    {
      id: 'stripe_connect',
      title: 'Connect Stripe for online payments',
      description: 'Set up Stripe Connect to accept court hire and membership payments online.',
      href: '/admin/payments?tab=settings',
      done: stripeConnectDone(organization, hasPaymentProcessing),
    },
  ];

  if (offerings.includes('court_hire')) {
    tasks.push({
      id: 'court_hire',
      title: 'Configure court hire',
      description: 'Set availability, pricing, and guest checkout for public court rentals.',
      href: '/admin/court-hire?tab=settings',
      done: courtHireRatesCount > 0,
    });
  }

  if (offerings.includes('memberships')) {
    tasks.push({
      id: 'memberships',
      title: 'Create membership plans',
      description: 'Define membership plans so members can join online or in person.',
      href: '/admin/members?tab=types',
      done: membershipPlansCount > 0,
    });
  }

  if (offerings.includes('inventory_hire')) {
    tasks.push({
      id: 'inventory',
      title: 'Add hireable inventory',
      description: 'Track equipment and items available for court hire add-ons or walk-up loans.',
      href: '/admin/inventory',
      done: inventoryItemsCount > 0,
    });
  }

  return tasks.map((task) => ({
    ...task,
    pageTourKey: resolveClubSetupTaskTourKey(task.id),
  }));
}
